import React from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Building2, Paperclip, ArrowUpRight, ExternalLink } from "lucide-react";
import { TypeTag, YearTag } from "@/components/Tag";

function formatDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch (_e) {
    return iso;
  }
}

function CardBody({ opp, showExpired }) {
  return (
    <>
      <div className="flex items-start justify-between gap-3 mb-3">
        <TypeTag type={opp.type} />
        <ArrowUpRight className="h-5 w-5 text-black opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      <h3 className="font-display text-xl font-bold leading-tight mb-2 text-black">
        {opp.title}
      </h3>

      {opp.provider && (
        <p className="flex items-center gap-1.5 text-sm text-zinc-600 mb-2">
          <Building2 className="h-3.5 w-3.5" />
          {opp.provider}
        </p>
      )}

      <p className="text-sm text-zinc-700 line-clamp-3 mb-4 flex-1">
        {opp.description}
      </p>

      <div className="space-y-2 mb-4 text-sm">
        {opp.event_date && (
          <div className="flex items-center gap-2 text-zinc-700">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(opp.event_date)}</span>
          </div>
        )}
        {opp.location && (
          <div className="flex items-center gap-2 text-zinc-700">
            <MapPin className="h-4 w-4" />
            <span>{opp.location}</span>
          </div>
        )}
        {opp.files && opp.files.length > 0 && (
          <div className="flex items-center gap-2 text-zinc-700">
            <Paperclip className="h-4 w-4" />
            <span>{opp.files.length} attachment{opp.files.length === 1 ? "" : "s"}</span>
          </div>
        )}
        {opp.links && opp.links.length > 0 && (
          <div className="flex items-center gap-2 text-zinc-700">
            <ExternalLink className="h-4 w-4" />
            <span>{opp.links.length} link{opp.links.length === 1 ? "" : "s"}</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 pt-3 border-t-2 border-black">
        {opp.year_groups?.map((y) => (
          <YearTag key={y} year={y} />
        ))}
        {showExpired && opp.is_expired && (
          <span className="brut-tag bg-[#FF6B6B] text-black ml-auto" data-testid="expired-badge">
            Expired
          </span>
        )}
      </div>
    </>
  );
}

export default function OpportunityCard({ opp, to, footer, showExpired = false, external = false }) {
  // Admin variant uses a non-link wrapper so inner Edit/Delete links remain valid HTML
  if (footer) {
    return (
      <div className="block group" data-testid={`opportunity-card-${opp.id}`}>
        <article className="brut-card-static p-6 h-full flex flex-col">
          <CardBody opp={opp} showExpired={showExpired} />
          {footer}
        </article>
      </div>
    );
  }

  if (external) {
    return (
      <a href={to} target="_blank" rel="noreferrer" className="block group" data-testid={`opportunity-card-${opp.id}`}>
        <article className="brut-card p-6 h-full flex flex-col">
          <CardBody opp={opp} showExpired={showExpired} />
        </article>
      </a>
    );
  }

  return (
    <Link to={to} className="block group" data-testid={`opportunity-card-${opp.id}`}>
      <article className="brut-card p-6 h-full flex flex-col">
        <CardBody opp={opp} showExpired={showExpired} />
      </article>
    </Link>
  );
}
